import PartMessage from '../ProductCard/PartMessage'
import { show, show as showNiceModal } from '@ebay/nice-modal-react'
import { Delete } from '@mui/icons-material'
import { RootState, store } from 'store'
import {
  removeProductThunk,
  useCartProduct,
  getInCartProductQty,
  getCartMode,
  isThereOnlyTheMiscellaneousCart,
} from 'cart'
import {
  ABSOLUTE_MAX_QUANTITY,
  I18n,
  MY_PLACE_SELLER,
  Translate,
  useBrandImage,
} from 'common'
import { NonCachedSearchType } from 'interfaces'
import { observer } from 'mobx-react-lite'
import { ReactElement, useContext, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { storeInfo } from 'src/helpers/StoreInfo'
import {
  SearchStoreContext,
  useSearchStore,
} from 'src/store/search/SearchStore'
import styled from 'styled-components'
import { QuantityPriceBreak } from '../quantityPriceBreak'
import SelectCartDrawer from '../SelectCartDrawer'
import miscellaneousVehicle from '../../../../../../src/features/search/Results/utils/miscellaneousVehicle'
import { StoreInstances } from '../../../../../../src/store/StoreInstancesContainer'
import { CartMode } from '../../../../../../src/store/models/ShoppingCartModels'
import { useBannerPromotionStore } from '../../../../../../src/store/promotion/BannerPromotionStore'
import { useUiStateStore } from '../../../../../../src/store/uiState/UiStateStore'
import { useUserStore } from '../../../../../../src/store/user/UserStore'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import { defaultTheme } from '../../../../../../src/theme/index'
import { Button } from '../../../../../../src/ui-components/forms/Button'
import { Price } from '../../../../../../src/ui-components/products/Price'
import { Thumbnail } from '../../../../../../src/ui-components/products/Thumbnail'
import { BundleTooltip } from '../BundleTooltip'
import AvailabilityInfo from './AvailabilityInfo'
import QuantitySelectorWrapper from './QuantitySelectorWrapper'
import { ProductInformationProps } from './interfaces'

const ProductInformation = observer(
  ({
    selectedProduct: product,
    sellerDetails = MY_PLACE_SELLER,
    vehicle = miscellaneousVehicle,
  }: ProductInformationProps): ReactElement => {
    const uiStore = useUiStateStore()
    const userStore = useUserStore()
    const displayExclTax = userStore?.preferences?.displayExclTax === 'true'

    const { setShowBrandLinksDrawer, isMultiLocationEnabled, showBundles } =
      uiStore
    const searchStore = useSearchStore()
    const { showPartsNotFitVehicle, lastNonCachedSearch } = searchStore
    const resultsStore = useContext(SearchStoreContext)

    const { handleBannerPromoDrawerOpenExpanded } = useBannerPromotionStore()
    const { description, fromYear, perCarQty, lineCode, partNumber } = product

    const cartProduct = useCartProduct({
      product,
      vehicle,
      sellerDetails,
    })

    const {
      updateQuantity,
      showLocationSelector,
      handleAddVehiclePartToCart,
      priceSpinner,
      yourCostAfterPriceBreaks,
      disableAddToCart,
      currentQty,
      setCurrentQty,
      inCartText,
      activeLocation,
      inStockSomewhere,
      orderIfNotAvailable,
      invDisplayState,
      getNonPrimaryLocationsQtyAvailable,
      getPrimaryLocationQtyAvailable,
      getOtherLocationsQtyAvailable,
    } = cartProduct

    const {
      list,
      coreCost,
      coreList,
      cost,
      otherPrices = [],
    } = activeLocation || {}

    const contactNumber: string =
      storeInfo.getContactAdressInfo()?.phone?.phoneNumber || ''

    const [thumbnail] = useBrandImage(product)
    const priceBreaks = activeLocation?.priceBreaks
    const visibleOtherPrices = (otherPrices ?? []).filter(
      (item) =>
        item.priceValue > 0 && item.priceCodeName?.toLowerCase() !== 'list'
    )

    const isApplicationPart = (): boolean => {
      return !showPartsNotFitVehicle
    }

    const showDrawer = (): void => {
      if (!inCart) {
        show(SelectCartDrawer, {
          product,
          handleAddVehiclePartToCart,
          activeLocation,
          ...(isMultiLocationEnabled() && { showLocationSelector }),
          seller: sellerDetails,
          currentVehicle: searchStore.currentVehicle,
        })
      }
    }

    const handleAddToCart = () => {
      handleBannerPromoDrawerOpenExpanded()
      // updateQuantity(currentQty, activeLocation)
      const isSingleCartMode = getCartMode() === CartMode.SINGLE_VEHICLE
      // TODO: Check if the below 3 if conditions can be rewritten into if-else block
      if (
        isSingleCartMode ||
        (isApplicationPart() &&
          vehicle?.engine &&
          lastNonCachedSearch !== NonCachedSearchType.searchByInterchange)
      ) {
        handleAddVehiclePartToCart(vehicle)
        return
      }
      if (!isThereOnlyTheMiscellaneousCart()) {
        showDrawer()
        return
      }

      if (!inCart) {
        handleAddVehiclePartToCart(miscellaneousVehicle)
      }
    }

    // shouldn't call when qty is less than intialSelectorQty
    const updatePriceBreaks = async (qty: number) => {
      if (qty < (activeLocation.displayMinQty ?? 0)) {
        StoreInstances.uiStore.displayErrorNotification(
          `Minimum quantity should be ${activeLocation.displayMinQty}`
        )
        return
      }
      await updateQuantity(qty, activeLocation)
      setCurrentQty(qty)
    }

    const handleClickQuantityPriceBreak = () => {
      showNiceModal(QuantityPriceBreak, {
        updatePriceBreaks,
        priceBreaks,
        image: thumbnail ?? undefined,
        partNumber,
        description,
        lineCode,
      })
    }

    useEffect(() => {
      resultsStore.setUpdatedPartInfoDetails({
        product,
        updatedQty: currentQty,
        updatedLocation: activeLocation,
      })
    }, [activeLocation, currentQty, product, resultsStore])

    const inCartQty = useSelector((state: RootState) =>
      getInCartProductQty(
        state,
        vehicle,
        sellerDetails?.coverageKey ?? '',
        product,
        activeLocation?.locationId
      )
    )
    const inCart = inCartQty > 0

    useEffect(() => {
      if (inCart) {
        setCurrentQty(inCartQty)
      }
    }, [inCart, inCartQty, setCurrentQty])

    const handleDeletePart = () => {
      store.dispatch(
        removeProductThunk({
          product,
          quantity: ABSOLUTE_MAX_QUANTITY,
          locationId: activeLocation.locationId.toString(),
          vehicle,
          seller: sellerDetails,
          removeFromAllLocations: true,
        })
      )
    }

    return (
      <ProductInformationContainer id="zoom-box">
        <ProductInformationItem>
          <Flex>
            <Title>{description}</Title>
            <BrandLogoContainer
              onClick={() => {
                setShowBrandLinksDrawer(true, product)
              }}
            >
              {thumbnail && (
                <Thumbnail urlOrBase64={thumbnail} width={72} height={35} />
              )}
            </BrandLogoContainer>
          </Flex>
          <ProductSpecicications>
            <ProductSpecicicationsText>
              <I18n name="Part#" />:{' '}
              <ProductSpecicicationsBold>
                {partNumber}
              </ProductSpecicicationsBold>
            </ProductSpecicicationsText>

            <ProductSpecicicationsText>
              <I18n name="lineCode" />:{' '}
              <ProductSpecicicationsBold>{lineCode}</ProductSpecicicationsBold>
            </ProductSpecicicationsText>

            <ProductSpecicicationsText>
              <I18n name="fitsYears" />:{' '}
              <ProductSpecicicationsBold>{fromYear}</ProductSpecicicationsBold>
            </ProductSpecicicationsText>

            <ProductSpecicicationsText>
              <I18n name="perCarQty" />:{' '}
              <ProductSpecicicationsBold>{perCarQty}</ProductSpecicicationsBold>
            </ProductSpecicicationsText>
          </ProductSpecicications>
          <PartMessage message={product?.partMessage?.[0]} fullWidth />
        </ProductInformationItem>

        <DividerContainer>
          <Divider />
        </DividerContainer>

        <CardItem>
          <CategoryContainer>
            <QuantityContainer>
              <Subtitle>{Translate('price')}</Subtitle>
              {showBundles() &&
                product?.eligibleBundles &&
                product?.eligibleBundles?.length > 0 && (
                  <BundleTooltip bundles={product?.eligibleBundles} />
                )}
              {uiStore.showYourCost() && (priceBreaks?.length ?? 0) > 0 && (
                <LocalLinkPriceBreak onClick={handleClickQuantityPriceBreak}>
                  <I18n name="qtyPriceBreak" />
                </LocalLinkPriceBreak>
              )}
            </QuantityContainer>

            <WrapperCosts>
              <MobileOnly>
                {showBundles() &&
                  product?.eligibleBundles &&
                  product?.eligibleBundles?.length > 0 && (
                    <BundleTooltip bundles={product?.eligibleBundles} />
                  )}
              </MobileOnly>
              <PricesLayout>
                <PriceRow>
                  <MobileOnlyPriceCell>
                    <PriceLabel>
                      <I18n name="Cost" />
                    </PriceLabel>
                    <PriceValueLarge>
                      <Price num={cost} />
                    </PriceValueLarge>
                  </MobileOnlyPriceCell>
                  {uiStore.showYourCost() && (
                    <PriceCell>
                      <PriceLabel>
                        <I18n name="yourCost" />
                      </PriceLabel>
                      <YourCostValue>
                        {priceSpinner ? (
                          <Spinner
                            style={{
                              height: '20px',
                              width: '20px',
                              color: defaultTheme.colors.warningBackground,
                            }}
                          />
                        ) : (
                          <PriceValueLarge>
                            <Price num={yourCostAfterPriceBreaks()} />
                          </PriceValueLarge>
                        )}
                        {displayExclTax && (
                          <ExclTaxText>
                            <I18n name="displayExclTax" />
                          </ExclTaxText>
                        )}
                      </YourCostValue>
                    </PriceCell>
                  )}
                  <PriceCell>
                    <PriceLabel>
                      <I18n name="listPrice" />
                    </PriceLabel>
                    <PriceValueLarge>
                      <Price num={list} />
                    </PriceValueLarge>
                  </PriceCell>
                </PriceRow>

                {((uiStore.showYourCost() && coreCost !== 0) ||
                  coreList !== 0) && (
                  <PriceRow>
                    <MobileOnlyPriceCell aria-hidden />
                    {uiStore.showYourCost() && coreCost !== 0 && (
                      <PriceCell>
                        <PriceLabelSecondary>
                          <I18n name="coreCost" />
                        </PriceLabelSecondary>
                        <PriceValueSecondary>
                          <Price num={coreCost} />
                        </PriceValueSecondary>
                      </PriceCell>
                    )}
                    <PriceCell>
                      {coreList !== 0 && (
                        <>
                          <PriceLabelSecondary>
                            <I18n name="coreList" />
                          </PriceLabelSecondary>
                          <PriceValueSecondary>
                            <Price num={coreList} />
                          </PriceValueSecondary>
                        </>
                      )}
                    </PriceCell>
                  </PriceRow>
                )}

                {visibleOtherPrices.length > 0 && (
                  <PriceRow>
                    {visibleOtherPrices.map((item) => (
                      <PriceCell key={item.priceCodeName}>
                        <PriceLabel>{item.priceCodeName}</PriceLabel>
                        <PriceValueLarge>
                          <Price num={item.priceValue} />
                        </PriceValueLarge>
                      </PriceCell>
                    ))}
                  </PriceRow>
                )}
              </PricesLayout>
            </WrapperCosts>
          </CategoryContainer>
        </CardItem>

        <DividerContainer>
          <Divider />
        </DividerContainer>

        <CardItem>
          <CategoryContainer>
            <AvailabilityInfo
              location={activeLocation}
              inStockSomewhere={inStockSomewhere}
              orderIfNotAvailable={orderIfNotAvailable}
              contactNumber={contactNumber}
              invDisplayState={invDisplayState}
              showLocationSelector={showLocationSelector}
              getNonPrimaryLocationsQtyAvailable={
                getNonPrimaryLocationsQtyAvailable
              }
              getPrimaryLocationQtyAvailable={getPrimaryLocationQtyAvailable}
              getOtherLocationsQtyAvailable={getOtherLocationsQtyAvailable}
            />
          </CategoryContainer>
        </CardItem>

        <DividerContainer>
          <Divider />
        </DividerContainer>

        <CardItem>
          <CategoryContainer>
            <QuantityContainer>
              <Subtitle>
                <I18n name="quantity" />
              </Subtitle>
            </QuantityContainer>
            <QuantityWrapper>
              <QuantitySelectorWrapper
                cartProduct={cartProduct}
                qty={currentQty}
                setQty={setCurrentQty}
                orderIfNotAvailable={orderIfNotAvailable}
              />
              {!inCart && (inStockSomewhere || orderIfNotAvailable) && (
                <ButtonWrapper>
                  <AddButton
                    onClick={handleAddToCart}
                    text="addToCart"
                    disabled={disableAddToCart}
                  />
                </ButtonWrapper>
              )}
              {!inStockSomewhere && !orderIfNotAvailable && (
                <ButtonWrapper>
                  <AddButton text="addToCart" disabled />
                </ButtonWrapper>
              )}
              {inCart && (
                <ButtonWrapper>
                  <AddButton text={inCartText} variant="success" />
                  <DeleteWrapper>
                    <DeleteButton onClick={handleDeletePart} />
                  </DeleteWrapper>
                </ButtonWrapper>
              )}
            </QuantityWrapper>
          </CategoryContainer>
        </CardItem>
      </ProductInformationContainer>
    )
  }
)

export default ProductInformation

const QuantityContainer = styled.div`
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ProductInformationContainer = styled.div`
  width: 100%;
  max-width: 754px;
  background: ${defaultTheme.colors.disabledBackground};
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    background: none;
  }
`

const ProductInformationItem = styled.div`
  min-height: 121px;
  padding: 16px 26px 32px 32px;
  text-align: initial;
  position: relative;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
    padding: 0px;
  }
`

const BrandLogoContainer = styled.div`
  cursor: pointer;
  flex-shrink: 0;
`

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
`

const QuantityWrapper = styled.div`
  width: 168px;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 100%;
  }
`

const LocalLink = styled.div`
  color: ${defaultTheme.colors.link};
  text-decoration: underline;
  cursor: pointer;
`
const LocalLinkPriceBreak = styled(LocalLink)`
  min-width: 109px;
`
const DividerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const Divider = styled.div`
  width: 96%;
  border-bottom: 2px solid ${defaultTheme.colors.greyMedium};
`
const Title = styled.h1`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 60px;
  color: ${defaultTheme.colors.primaryText};
  margin-bottom: 3px;
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
  word-break: break-word;
`
const ProductSpecicications = styled.div`
  display: flex;
  align-items: center;
`

const ProductSpecicicationsText = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin-right: 24px;
  text-wrap: nowrap;
`
const ProductSpecicicationsBold = styled.span`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin-right: 24px;
  font-weight: bold;
`

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 90px;
`

const Subtitle = styled.h4`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  margin-bottom: 3px;
`
const PricesLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const PriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px 48px;
  width: 100%;
`

const PriceCell = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    justify-content: space-between;
    width: 100%;
  }
`

const MobileOnlyPriceCell = styled(PriceCell)`
  display: none;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
  }
`

const PriceLabel = styled.p`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  margin: 0;
  margin-right: 23px;
  width: 120px;
  flex-shrink: 0;
  word-wrap: break-word;
  white-space: normal;
`

const PriceLabelSecondary = styled(PriceLabel)`
  font-weight: 400;
  font-size: 16px;
`

const PriceValueLarge = styled(PriceLabel)`
  font-size: 24px;
  width: fit-content;
  flex-shrink: 0;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    font-size: 18px;
  }
`

const PriceValueSecondary = styled(PriceLabelSecondary)`
  font-size: 18px;
  width: fit-content;
  flex-shrink: 0;
`

const CardItem = styled(ProductInformationItem)`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  padding: 32px 24px;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    padding: 20px;
  }
`
const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 0;
  width: 168px;
`
const DeleteWrapper = styled.div`
  padding: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${defaultTheme.colors.siteBackground};
  cursor: pointer;
  border-radius: 3px;
`
const AddButton = styled(Button)`
  width: 100%;
  box-sizing: border-box;
`
const DeleteButton = styled(Delete)`
  color: ${defaultTheme.colors.greyDark};
`

const WrapperCosts = styled.div`
  display: flex;
  flex-direction: column;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 100%;
  }
`
// const BundleTag = styled.div`
//   display: inline-flex;
//   align-items: center;
//   gap: 6px;
//   background-color: #f6e8b1;
//   color: #000;
//   font-weight: 500;
//   font-size: 14px;
//   padding: 4px 10px;
//   border-radius: 4px;
//   cursor: default;
//   width: fit-content;
//   transition: all 0.2s ease;
//   margin-bottom: 8px;

//   &:hover {
//     background-color: #000;
//     color: #f6e8b1;

//     svg {
//       color: #f6e8b1;
//     }
//   }
// `

// const TagIcon = styled(LocalOfferIcon)`
//   color: #000;
//   font-size: 16px;
// `
const MobileOnly = styled.div`
  display: none;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: block;
  }
`

const YourCostValue = styled.div`
  display: flex;
  flex-direction: column;
`

const ExclTaxText = styled.span`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.columnBorder};
`
